import { Hit } from 'instantsearch.js'
import React, { createContext, useContext } from 'react'
import { Brand, Category } from 'types/graphql'
import { useAlgoliaSearch } from '../hooks/useAlgoliaSearch'
import { AutocompleteQuerySuggestionsHit } from '../types/Hits'
import { AlgoliaConfigProvider } from './AlgoliaConfigProvider'

interface Context {
  activeKey: string
  brandHits: Hit<Brand>[]
  categoryHits: Hit<Category>[]
  clearSearch: () => void
  closeModal: () => void
  handleArrowPress: (action: 'increment' | 'decrement') => (e: KeyboardEvent) => void
  handleCategoryClick: (category: Category) => void
  handleEnterPress: (e: KeyboardEvent, global?: boolean) => void
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  inputFocused: boolean
  isEmpty: boolean
  isModalOpen: boolean
  isNoResults: boolean
  isResultsReturned: boolean
  isSearching: boolean
  loading: boolean
  openModal: (showSuggestions?: boolean) => void
  query: string
  resultKeys: string[]
  searchForText: (text: string, skipAnalytics?: boolean, global?: boolean) => void
  showSuggestions: boolean
  suggestionHits: AutocompleteQuerySuggestionsHit<string>[]
  toggleInputFocus: (focus: boolean) => void
}

export const AlgoliaSearchContext = createContext<Context>({
  activeKey: '',
  brandHits: [],
  categoryHits: [],
  clearSearch: () => {},
  closeModal: () => {},
  handleArrowPress: () => () => {},
  handleCategoryClick: () => {},
  handleEnterPress: () => () => {},
  handleInputChange: () => {},
  inputFocused: false,
  isEmpty: false,
  isModalOpen: false,
  isNoResults: false,
  isResultsReturned: false,
  isSearching: false,
  loading: false,
  openModal: () => {},
  query: '',
  resultKeys: [],
  searchForText: () => {},
  showSuggestions: false,
  suggestionHits: [],
  toggleInputFocus: () => {}
})

interface Props {
  children: React.ReactNode
}

// useAlgoliaSearch hook needs access to algolia config context
const AlgoliaConfigWrapper = ({ children }: Props) => {
  const values = useAlgoliaSearch()
  return <AlgoliaSearchContext.Provider value={{ ...values }}>{children}</AlgoliaSearchContext.Provider>
}

export const AlgoliaSearchProvider = ({ children }: Props) => {
  return (
    <AlgoliaConfigProvider>
      <AlgoliaConfigWrapper>{children}</AlgoliaConfigWrapper>
    </AlgoliaConfigProvider>
  )
}

export const useAlgoliaSearchContext = () => useContext(AlgoliaSearchContext)
